import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Ring2 } from 'ldrs/react';
import 'ldrs/react/Ring2.css';
import { SvgIcon } from '@@components';
import type { RootState } from '@@store/index';
import type { StreamingSegment } from '@@store/slices/chatSlice';
import { useThemeToken } from '@/hooks';
import './index.less';

export interface ToolCallsBlockProps {
    // 传统模式：通过 messageId 从 history 读取
    messageId?: string;
    // 内联模式：直接传入 toolActivities（用于 segments 渲染）
    inlineActivities?: StreamingSegment['toolActivities'];
    inlineProcessing?: boolean;
    toolCount?: number;
}

const ToolCallsBlock: React.FC<ToolCallsBlockProps> = ({
    messageId,
    inlineActivities,
    inlineProcessing,
    toolCount: propToolCount
}) => {
    const currentActivities = useSelector((state: RootState) => state.chat.toolActivities);
    const streamError = useSelector((state: RootState) => state.chat.streamError);
    const activitiesHistory = useSelector((state: RootState) => state.chat.toolActivitiesHistory);
    const chatStatus = useSelector((state: RootState) => state.chat.status);

    const { colorReferencePrimary, colorErrorText, colorTextSecondary } = useThemeToken();

    // 内联模式：直接使用传入的 activities
    const isInlineMode = !!inlineActivities;

    const { source, errorSource } = useMemo(() => {
        if (isInlineMode) {
            return { source: inlineActivities, errorSource: null };
        }
        if (messageId && activitiesHistory[messageId]) {
            return {
                source: activitiesHistory[messageId],
                errorSource: activitiesHistory[messageId].streamError
            };
        }
        return { source: currentActivities, errorSource: streamError };
    }, [ isInlineMode, inlineActivities, messageId, activitiesHistory, currentActivities, streamError ]);

    const hasTools = !!source && source.order && source.order.length > 0;
    const hasError = !!errorSource;
    const toolCount = propToolCount ?? (hasTools ? source!.order.length : 0);

    if (!hasTools && !hasError) return null;

    const isHistory = !!messageId;
    const isError = !!errorSource;
    const isProcessing = isInlineMode
        ? !!inlineProcessing
        : (!isHistory && !isError && chatStatus === 'streaming' && hasTools);
    const isCompleted = !isProcessing && !isError;

    return (
        <ToolCallsInner
            source={source!}
            errorSource={errorSource}
            toolCount={toolCount}
            isHistory={isHistory || isInlineMode}
            isProcessing={isProcessing}
            isCompleted={isCompleted}
            isError={isError}
            colorReferencePrimary={colorReferencePrimary}
            colorErrorText={colorErrorText}
            colorTextSecondary={colorTextSecondary}
        />
    );
};

// 内部渲染组件，管理折叠状态
const ToolCallsInner: React.FC<{
    source: {
        byId: Record<string, { id: string; thinkTitle?: string; thinking?: string }>;
        order: string[];
        completed?: boolean;
    };
    errorSource: any;
    toolCount: number;
    isHistory: boolean;
    isProcessing: boolean;
    isCompleted: boolean;
    isError: boolean;
    colorReferencePrimary: string;
    colorErrorText: string;
    colorTextSecondary: string;
}> = ({
    source,
    errorSource,
    toolCount,
    isHistory,
    isProcessing,
    isCompleted,
    isError,
    colorReferencePrimary,
    colorErrorText,
    colorTextSecondary
}) => {
    const [ expanded, setExpanded ] = React.useState(!isHistory && !isCompleted);

    React.useEffect(() => {
        if (isCompleted && !isProcessing) {
            setExpanded(false);
        }
    }, [ isCompleted, isProcessing ]);

    React.useEffect(() => {
        if (isProcessing) {
            setExpanded(true);
        }
    }, [ isProcessing ]);

    const labelText = isError
        ? 'Error'
        : isProcessing
            ? 'Running tools...'
            : `Ran ${toolCount} tool${toolCount > 1 ? 's' : ''}`;

    return (
        <div className="tool-calls-block" role="status" aria-live="polite" aria-label="Tool execution status">
            <div
                className="tool-calls-header"
                onClick={() => setExpanded(!expanded)}
            >
                <span className="tool-calls-header-icon">
                    {isProcessing && (
                        <Ring2
                            size="14"
                            stroke="2"
                            strokeLength="0.25"
                            bgOpacity="0.1"
                            speed="0.8"
                            color={colorReferencePrimary}
                        />
                    )}
                    {isCompleted && (
                        <SvgIcon name="think-completed" color={colorReferencePrimary} />
                    )}
                    {isError && (
                        <SvgIcon name="error" color={colorErrorText} />
                    )}
                </span>
                <span
                    className="tool-calls-header-label"
                    style={{ color: isError ? colorErrorText : colorTextSecondary }}
                >
                    {labelText}
                </span>
                <span className={`tool-calls-header-arrow ${expanded ? 'expanded' : ''}`}>
                    <SvgIcon name="v-right" />
                </span>
            </div>
            {expanded && (
                <div className="tool-calls-body">
                    {isError && errorSource?.content && (
                        <div className="tool-calls-error" style={{ color: colorErrorText }}>
                            {errorSource.content}
                        </div>
                    )}
                    {source.order.map((id: string) => {
                        const item = source.byId[id];
                        if (!item) return null;
                        return (
                            <div key={id} className="tool-calls-step">
                                {item.thinkTitle && (
                                    <div className="tool-calls-step-title">
                                        {item.thinkTitle}
                                    </div>
                                )}
                                {item.thinking && (
                                    <div className="tool-calls-step-desc">
                                        {item.thinking}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ToolCallsBlock;
